import { removeQuotesFromText, tryCatch } from '@whistler-ui/k2/helpers';
import { SilentParam } from '@whistler-ui/k2/types/common';

import {
  NetworkDetail,
  NetworkDetailsRcsVerifiedPayload,
  NetworkSettings,
  NetworkSettingsRequest,
  NsxReviewIdentityView,
  NsxReviewsApprovePayload,
  NsxReviewsRejectPayload,
  OrganisationContacts,
  RcsClientSecretResponse,
  RcsOnboardingInfoResponse,
  SignUpRequestPayload,
  SignUpResponsePayload,
  User,
} from '../types';
import { Organisation } from '../types/organisation';
import { requestWithOptions, requestWithOptionsNoAuth } from './helper';

const basePath = import.meta.env.REACT_APP_API_ENDPOINT_ACCOUNTS || '/';
const alternatePath =
  import.meta.env.REACT_APP_ALTERNATE_API_ENDPOINT_ACCOUNTS || '/';

export const updateUser = async (payload: any) => {
  const options = {
    body: JSON.stringify(payload),
    method: 'PUT',
  };

  const path = `${basePath}/users`;

  const response = await requestWithOptions(path, options);
  return response;
};

export const getNetworkDetails = async () => {
  const options = {
    method: 'GET',
  };

  const path = `${basePath}/network_details`;

  const response = await requestWithOptions(path, options);
  return response;
};

export const getNetworkDetailById = async (id: any) => {
  const options = {
    method: 'GET',
  };

  const path = `${basePath}/network_details/${id}`;

  const response = await requestWithOptions(path, options);
  return response;
};

export const putNetworkDetails = async (networkDetails: any) => {
  const options = {
    method: 'PUT',
    body: JSON.stringify(networkDetails),
  };

  const path = `${basePath}/network_details`;

  return requestWithOptions(path, options);
};

export const putNetworkSettings = async (payload: NetworkSettingsRequest) => {
  const options = { method: 'PUT', body: JSON.stringify(payload) };
  const path = `${basePath}/network_settings`;
  const result = await requestWithOptions(path, options);
  return result as NetworkSettings;
};

export const postNetworkSettings = async (payload: NetworkSettingsRequest) => {
  const options = { method: 'POST', body: JSON.stringify(payload) };
  const path = `${basePath}/network_settings`;
  const result = await requestWithOptions(path, options);
  return result as NetworkSettings;
};

export const getNetworkSettings = async () => {
  const options = { method: 'GET' };
  const path = `${basePath}/network_settings`;
  const result = await requestWithOptions(path, options);
  return result as NetworkSettings;
};

export const postNetworkDetails = async (networkDetails: any) => {
  const options = {
    method: 'POST',
    body: JSON.stringify(networkDetails),
  };

  const path = `${basePath}/network_details`;

  return requestWithOptions(path, options);
};

export const deleteNetworkDetails = async (networkDetailsId: any) => {
  const options = {
    method: 'DELETE',
  };

  const path = `${basePath}/network_details/${networkDetailsId}`;
  const response = await requestWithOptions(path, options);

  return response;
};

/**
 * Sets the NetLync-only `rcs_verified` flag on one network_details row.
 * Dedicated endpoint (UES-12200): the flag is ignored on the generic
 * POST/PUT /network_details and only NetLync users impersonating the carrier
 * are authorised here.
 */
export const putNetworkDetailsRcsVerified = async ({
  id,
  rcs_verified,
}: NetworkDetailsRcsVerifiedPayload) => {
  const options = { method: 'PUT', body: JSON.stringify({ rcs_verified }) };
  const path = `${basePath}/network_details/${id}/rcs-verified`;
  const response = await requestWithOptions(path, options);
  return response as NetworkDetail;
};

export const getLoggedUser = async ({ silent }: SilentParam) => {
  const { data, error } = await tryCatch<User>(async () => {
    const options = {
      method: 'GET',
    };
    const path = `${basePath}/users/current`;
    const result = await requestWithOptions(path, options);
    return result;
  });

  if (error) {
    if (silent) {
      return null;
    }

    throw error;
  }

  return data;
};

export const postRegisterUser = async ({
  payload,
  alternateUrl = false,
}: {
  payload: SignUpRequestPayload;
  alternateUrl?: boolean;
}) => {
  const options = {
    method: 'POST',
    body: JSON.stringify(payload),
  };

  const path = alternateUrl
    ? `${alternatePath}/register`
    : `${basePath}/register`;

  const result = await requestWithOptionsNoAuth(path, options);
  return result as SignUpResponsePayload;
};

export const createContact = async ({
  payload,
  silent,
}: SilentParam<{ payload: any }>) => {
  try {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };

    const path = `${basePath}/organisation_contacts`;

    const response = await requestWithOptions(path, options);
    return response;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);

    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }

    throw error;
  }
};

export const updateContact = async ({
  payload,
  silent,
}: SilentParam<{ payload: any }>) => {
  try {
    const options = {
      method: 'PUT',
      body: JSON.stringify(payload),
    };

    const path = `${basePath}/organisation_contacts`;

    const response = await requestWithOptions(path, options);
    return response;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);

    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }

    throw error;
  }
};

export const getOrgContacts = async () => {
  const options = {
    method: 'GET',
  };

  const path = `${basePath}/organisation_contacts`;

  const response = await requestWithOptions(path, options);
  return response as OrganisationContacts;
};

export const postRegisterConfirm = async () => {
  const options = {
    method: 'POST',
  };

  const path = `${basePath}/register/confirm`;

  window.localStorage.setItem('first-time-loading', 'true');

  const response = await requestWithOptions(path, options);
  return response;
};

/**
 * Fetches the details of an organisation by its ID.
 */
export const getOrganisationDetails = async ({
  silent = false,
  organisation_id,
}: SilentParam<{
  organisation_id: string;
}>): Promise<Organisation> | null => {
  try {
    const options = { method: 'GET' };
    const path = new URL(`${basePath}/organisations/${organisation_id}`);
    const result = await requestWithOptions(path, options);
    return result as Organisation;
  } catch (e) {
    const msg = removeQuotesFromText((e as Error | TypeError).message);

    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }

    throw e;
  }
};

export const getNsxReviews = async () => {
  const options = { method: 'GET' };
  const path = `${basePath}/nsx/reviews`;
  const response = await requestWithOptions(path, options);
  return response as NsxReviewIdentityView[];
};

/**
 * Read-only RCS onboarding info for the logged user's organisation. Phase 1
 * serves `acs_fqdns`; the remaining fields are null until UES-12629.
 */
export const getRcsOnboardingInfo = async () => {
  const options = { method: 'GET' };
  const path = `${basePath}/rcs`;
  const response = await requestWithOptions(path, options);
  return response as RcsOnboardingInfoResponse;
};

/**
 * On-demand read of the Cognito app-client secret. ADMINISTRATOR-scoped
 * (UES-12629); 404 when the organisation has no app client yet.
 */
export const getRcsCognitoClientSecret = async () => {
  const options = { method: 'GET' };
  const path = `${basePath}/rcs/cognito/client-secret`;
  const response = await requestWithOptions(path, options);
  return response as RcsClientSecretResponse;
};

export const approveNsxReviews = async (payload: NsxReviewsApprovePayload) => {
  const options = { method: 'POST', body: JSON.stringify(payload) };
  const path = `${basePath}/nsx/reviews/approve`;
  const response = await requestWithOptions(path, options);
  return response as NsxReviewIdentityView[];
};

export const rejectNsxReviews = async (payload: NsxReviewsRejectPayload) => {
  const options = { method: 'POST', body: JSON.stringify(payload) };
  const path = `${basePath}/nsx/reviews/reject`;
  const response = await requestWithOptions(path, options);
  return response as NsxReviewIdentityView[];
};

/**
 * Requests the upgrade of the logged user's organisation to Production.
 * Backend transitions the organisation from Trial to AwaitingProductionApproval.
 */
export const putOrganisationUpgrade = async () => {
  const options = { method: 'PUT' };
  const path = `${basePath}/organisations/upgrade`;
  const response = await requestWithOptions(path, options);
  return response;
};

export const updateServerSelection = async (payload: any) => {
  const options = {
    method: 'PUT',
    body: JSON.stringify({
      organisation_id: payload.id,
      entitlement_server: payload.entitlement_server,
    }),
  };

  const path = `${basePath}/organisations/entitlement_server`;

  const response = await requestWithOptions(path, options);
  return response;
};
